import { Controller } from "@hotwired/stimulus"
import { MessagingMixin } from "./chat/messaging"
import { UIMixin } from "./chat/ui"
import { ConnectionMixin } from "./chat/connection"
import { isIOSWebView } from "../lib/ios_focus_guard"

// Chat Controller - 실시간 채팅 (모듈화된 구조)
// 414줄 → 60줄로 축소 (메서드는 모듈로 분리)
export default class extends Controller {
  static targets = ["messages", "messagesInner", "input", "form", "submitButton", "olderRow", "olderButton", "footer"]
  static values = {
    chatId: Number,
    currentUserId: Number,
    locationVerifiedUntil: String
  }

  connect() {
    // 초기화
    this.isConnected = false
    this.lastDraft = null
    this.pendingClientId = null
    this.sendTimeout = null
    this.lastMessageTimestamp = null
    this.httpFallbackSubmitting = false
    this.locationVerificationTimeout = null

    // 모듈 메서드 바인딩 (this 컨텍스트 유지)
    this.bindMixins()

    // 초기 설정
    this.setupCable()
    this.scheduleLocationVerificationExpiry()
    this.handleGpsVerified = this.handleGpsVerified.bind(this)
    window.addEventListener("gps:verified", this.handleGpsVerified)
    this.normalizeExistingMessages()
    this.initializeLastMessageTimestamp()
    this.scrollToBottom()
    this.observeOlder()
    this.trackFooterHeight()

    // Android only: 메시지 영역 스크롤 시 키보드 dismiss
    // iOS는 WKWebView가 스크롤 시 키보드를 자동으로 dismiss함
    // (iOS에서 적용 시, 자동 레이아웃 조정이 scroll로 감지되어 포커스 직후 blur 발생)
    if (!isIOSWebView()) {
      this.setupScrollDismissKeyboard()
    }
  }

  disconnect() {
    if (this.locationVerificationTimeout) {
      window.clearTimeout(this.locationVerificationTimeout)
      this.locationVerificationTimeout = null
    }

    window.removeEventListener("gps:verified", this.handleGpsVerified)

    this.teardownCable()
    this.olderObserver?.disconnect()
    this.footerObserver?.disconnect()
    this.cleanupScrollDismissKeyboard()
  }

  // The thread's bottom padding follows the fixed footer's real height (tips closed, action row, input growth)
  trackFooterHeight() {
    if (!this.hasFooterTarget || !("ResizeObserver" in window)) return
    const apply = () => this.element.style.setProperty("--chat-footer-h", `${this.footerTarget.offsetHeight}px`)
    this.footerObserver = new ResizeObserver(apply)
    this.footerObserver.observe(this.footerTarget)
    apply()
  }

  /**
   * 메시지 영역 스크롤 시 키보드 dismiss (Android UX)
   * adjustNothing 모드에서 키보드가 자동으로 닫히지 않으므로
   * 사용자가 메시지를 스크롤할 때 입력창 blur로 키보드를 닫음
   */
  setupScrollDismissKeyboard() {
    this.scrollDismissHandler = () => {
      const activeElement = document.activeElement
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        activeElement.blur()
      }
    }
    this.messagesTarget.addEventListener('scroll', this.scrollDismissHandler, { passive: true })
  }

  cleanupScrollDismissKeyboard() {
    if (this.scrollDismissHandler) {
      this.messagesTarget.removeEventListener('scroll', this.scrollDismissHandler)
    }
  }

  // ⭐ 모듈 메서드를 컨트롤러 인스턴스에 바인딩
  bindMixins() {
    // Messaging 모듈
    Object.keys(MessagingMixin).forEach(key => {
      this[key] = MessagingMixin[key].bind(this)
    })

    // UI 모듈
    Object.keys(UIMixin).forEach(key => {
      this[key] = UIMixin[key].bind(this)
    })

    // Connection 모듈
    Object.keys(ConnectionMixin).forEach(key => {
      this[key] = ConnectionMixin[key].bind(this)
    })
  }

  // 📌 모듈에서 제공하는 메서드들:
  //
  // === Messaging (chat/messaging.js) ===
  // - sendMessage(event)
  // - handleSendSuccess()
  // - handleSendFailure(errorMessage)
  // - retryLastMessage()
  // - fallbackToHTTP()
  // - showErrorToast(message)
  //
  // === UI (chat/ui.js) ===
  // - appendMessage(data)
  // - createMessageHTML(data, isOwnMessage, showTimestamp)
  // - scrollToBottom()
  // - scrollToLatest()
  // - normalizeExistingMessages()
  // - normalizeLegacyBubble(article, bubble)
  // - ensureArticleTimestamp(article)
  // - initializeLastMessageTimestamp()
  // - escapeHtml(text)
  //
  // === Connection (chat/connection.js) ===
  // - setupCable()
  // - handleInput(event)
}
